import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Phone, Shield, Facebook, Instagram } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import logoThum from '../assets/logoMDX1.jpg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Training', href: '#courses' },
    { name: 'E-Learning', href: '#' },
    { name: 'Equipment', href: 'https://mdxequipment.com/collections/flame-retardant-resistant-clothing' },
    { name: 'Platform Software', href: '#safetysoftware' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/MDXWorkforceConsultingCenterandTrainingAcademy/', name: 'Facebook' },
    { icon: Instagram, href: '#', name: 'Instagram' },
  ];



  
  return (
    <>
      {/* Meta viewport para Safari iPhone */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />

      <motion.header
        className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
          isScrolled ? 'bg-gradient-to-r from-orange-700 to-orange-900 shadow-lg' : 'bg-gradient-to-r from-orange-600 to-orange-800'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top bar: Contact info + social (mobile stacked, desktop inline) */}
          <div className="flex flex-col md:flex-row items-center justify-between text-white text-xs sm:text-sm py-1 md:py-2 select-none">
            {/* Contact info */}
            <div className="flex flex-wrap justify-center md:justify-start gap-3 md:gap-6 items-center max-w-full md:max-w-none">
              <a href="tel:+19799855388" className="flex items-center gap-1 hover:text-orange-300 transition-colors whitespace-nowrap">
                <Phone className="w-4 h-4" />
                (979) 985-5388
              </a>
              <a href="mailto:info@mdxsafetyinc.com" className="flex items-center gap-1 hover:text-orange-300 transition-colors whitespace-nowrap">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                info@mdxsafetyinc.com
              </a>
              <a
                href="https://www.google.com/maps?q=1722 Broadmoor Dr. Suite 202 Bryan, Texas 77802"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-orange-300 transition-colors whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                1722 Broadmoor Dr. Suite 202 Bryan, Texas 77802
              </a>
            </div>

            {/* Social icons */}
            <div className="mt-2 md:mt-0 flex justify-center md:justify-end gap-3">
              {socialLinks.map(({ icon: Icon, href, name }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-orange-500 hover:text-white transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Main nav bar */}
          <div className="flex items-center justify-between py-3 md:py-4">
            {/* Logo */}
          </div>

          {/* Mobile menu */}
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: isMenuOpen ? 'auto' : 0, opacity: isMenuOpen ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden"
          >
            <div className="flex flex-col space-y-4 pb-4">
              {navItems.map(({ name, href }) => (
                <a
                  key={name}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-gray-800 font-semibold rounded-md hover:bg-orange-100 transition-colors"
                >
                  {name}
                </a>
              ))}
              <a
                href="https://wa.me/19795755999?text=Hello!%20I'd%20like%20more%20information%20about%20your%20courses:%20"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className="block text-center bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transition-shadow"
              >
                Get Quote
              </a>
            </div>
          </motion.nav>

          {/* Trusted partner badge (desktop only) */}
          <div className="flex items-center justify-between h-12 rounded-2x1  border-blue-10/150 text-white rounded-2xl">
            <motion.div
              className="flex items-center gap-3"
              whileHover={{ scale: 1.05 }}
            >

              <div className="w-60 h-30 ">
                <img
                  src={logoThum}
                  alt="Logo Thumbnail"
                  className="h-full w-full object-contain rounded-2xl scale-[0.7] sm:scale-100 transition-transform duration-300 ease-in-out bg-gradient-to-r from-blue-100 to-blue-150 text-white border border-blue-100/40 "
                />
              </div>

            </motion.div>

            <nav className="hidden md:flex items-center gap-8">


              {navItems.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className={`transition-colors duration-300 hover:text-orange-200 ${isScrolled ? 'text-gray-100' : 'text-white'}`}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.span
                    className="relative z-10"
                    initial="rest"
                    whileHover="hover"
                    animate="rest"
                  >
                    {item.name}
                    <motion.span
                      className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-white"
                      variants={{
                        rest: { width: 0, x: '-50%' },
                        hover: { width: '100%', x: '-50%', transition: { duration: 0.3, ease: "easeOut" } },
                      }}
                    />
                  </motion.span>
                </motion.a>
              ))}

              <motion.a
                href="https://wa.me/19795755999?text=Hello!%20I'd%20like%20more%20information%20about%20your%20courses:%20"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaWhatsapp className="w-6 h-6" />
                Get Quote
              </motion.a>
            </nav>

            <button
              className={`md:hidden p-2 rounded-lg ${isScrolled ? 'text-gray-400' : 'text-white'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          <div className="flex-1 flex items-center w-full ">
            <div className="flex-1">
              <h1 className={`text-2xl font-bold ${isScrolled ? 'text-gray-100' : 'text-white'}`}
                style={{ fontFamily: "'Montserrat', sans-serif", fontSize: isScrolled ? '1.5rem' : '2rem' }}>
                {/* Workforce Academy */}
              </h1>
              <p className={`text-sm ${isScrolled ? 'text-gray-600' : 'text-orange-200'}`}>
              </p>
            </div>

            <motion.div
              className="hidden md:inline-flex items-center gap-2 bg-orange-200/20 backdrop-blur-sm border border-orange-500/30 rounded-full px-4 py-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Shield className="text-orange-200" />
              <span className="text-sm font-semibold text-orange-200">
                Trusted Safety Partner Since 2008 - English - Spanish
              </span>
            </motion.div>

            <div className="flex-1"></div>
          </div>

        </div>
      </motion.header>
    </>
  );
};

export default Header;




// import React, { useState, useEffect } from 'react';
// import { motion } from 'framer-motion';
// import { Menu, X, Phone, Shield, Facebook, Instagram } from 'lucide-react';
// import WorkingHours from './WorkingHours';
// import { FaWhatsapp } from 'react-icons/fa';
// import logoThum from '../assets/logoMDX1.jpg';

// const Header = () => {
//   const [isMenuOpen, setIsMenuOpen] = useState(false);
//   const [isScrolled, setIsScrolled] = useState(false);

//   useEffect(() => {
//     const handleScroll = () => {
//       setIsScrolled(window.scrollY > 50);
//     };
//     window.addEventListener('scroll', handleScroll);
//     return () => window.removeEventListener('scroll', handleScroll);
//   }, []);

//   const navItems = [
//     { name: 'Home', href: '#home' },
//     { name: 'Services', href: '#services' },
//     { name: 'Training', href: '#courses' },
//     { name: 'Platform Software', href: '#safetysoftware' },
//     { name: 'About', href: '#about' },
//     { name: 'Contact', href: '#contact' }
//   ];

//   const socialLinks = [
//     { icon: Facebook, href: 'https://www.facebook.com/MDXWorkforceConsultingCenterandTrainingAcademy/', name: 'Facebook' },
//     { icon: Instagram, href: '#', name: 'Instagram' }
//   ];

//   return (
//     <>
//       <link
//         href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700&display=swap"
//         rel="stylesheet"
//       />

//       <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
      
//       <motion.header
//         className={`fixed w-full z-50 transition-all duration-300 ${isScrolled
//           ? 'bg-gradient-to-r from-orange-600 to-orange-900 text-white py-2 px-4 '
//           : 'bg-gradient-to-r from-orange-600 to-orange-900 text-white py-2 px-4 '
//           }`}
//         initial={{ y: -100 }}
//         animate={{ y: 0 }}
//         transition={{ duration: 0.6 }}
//       >

//         <div className="container mx-auto px-4 " >

//           {/* first header */}
//           <div className="container mx-auto relative flex items-center text-sm " style={{ minHeight: '40px' }}>
//             {/* block social media - align to right */}
//             <div className="absolute right-0 top-1/2 transform -translate-y-1/2 flex gap-8 ">
//               <div className="flex justify-end gap-3">
//                 <div className="hidden md:block">
//                   <div className="flex gap-2">
//                     {socialLinks.map((social, index) => {
//                       const IconComponent = social.icon;
//                       return (
//                         <motion.a
//                           key={social.name}
//                           href={social.href}
//                           className="w-10 h-10 bg-white/10 border border-white/20 rounded-xl flex items-center justify-center text-gray-300 hover:text-white hover:bg-orange-500 transition-all duration-300"
//                           whileHover={{ scale: 1.1 }}
//                           whileTap={{ scale: 0.9 }}
//                           initial={{ opacity: 0, scale: 0 }}
//                           whileInView={{ opacity: 1, scale: 1 }}
//                           transition={{ delay: index * 0.1 + 0.4 }}
//                           viewport={{ once: true }}
//                         >
//                           <IconComponent className="w-4 h-4" />
//                         </motion.a>
//                       );
//                     })}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Block dates - absolute center */}
// <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col md:flex-row items-center justify-center gap-6 px-4">
//   {/* Cada opción en su propio div */}
//   <div className="flex items-center gap-4 w-full md:w-auto justify-center">
//     <Phone className="w-4 h-4 text-white font-semibold" />
//     <span className="font-semibold text-white hover:text-[#a1a09a] transition-colors">(979) 985-5388</span>
//   </div>
//   <div className="flex items-center gap-2 w-full md:w-auto justify-center">
//     <a href="mailto:info@mdxsafetyinc.com" className="flex items-center gap-2 text-white hover:text-[#a1a09a] transition-colors">
//       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
//         <polyline points="22,6 12,13 2,6"></polyline>
//       </svg>
//       <span className="font-semibold">info@mdxsafetyinc.com</span>
//     </a>
//   </div>
//   <div className="flex items-center gap-2 w-full md:w-auto justify-center">
//     <a href="https://www.google.com/maps?q=1722 Broadmoor Dr. Suite 202 Bryan, Texas 77802" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white hover:text-[#a1a09a] transition-colors">
//       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//         <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
//         <circle cx="12" cy="10" r="3"></circle>
//       </svg>
//       <span className="font-semibold">1722 Broadmoor Dr. Suite 202 Bryan, Texas 77802</span>
//     </a>
//   </div>
// </div>
//           </div>

//           {/* header img logo */}
//           <div className="flex items-center justify-between h-12 rounded-2x1  border-blue-10/150 text-white rounded-2xl">
//             <motion.div
//               className="flex items-center gap-3"
//               whileHover={{ scale: 1.05 }}
//             >
//               <div className="w-60 h-30 bg-gradient-to-r from-blue-100 to-blue-150 text-white rounded-2xl flex items-center justify-center border border-blue-100/40 overflow-hidden">
//                 <img src={logoThum} alt="Logo Thumbnail" className="h-full w-full object-contain rounded-2xl" />
//               </div>
//             </motion.div>

//             <nav className="hidden md:flex items-center gap-8">
//               {navItems.map((item, index) => (
//                 <motion.a
//                   key={item.name}
//                   href={item.href}
//                   className={`font-semibold transition-colors duration-300 hover:text-orange-500 ${isScrolled ? 'text-gray-100' : 'text-white'}`}
//                   initial={{ opacity: 0, y: -20 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: index * 0.1 + 0.3 }}
//                   whileHover={{ scale: 1.05 }}
//                 >
//                   {item.name}
//                 </motion.a>
//               ))}

//               <motion.a
//                 href="https://wa.me/19795755999?text=Hello!%20I'd%20like%20more%20information%20about%20your%20courses:%20"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="flex items-center gap-3 px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//               >
//                 <FaWhatsapp className="w-6 h-6" />
//                 Get Quote
//               </motion.a>
//             </nav>

//             <button
//               className={`md:hidden p-2 rounded-lg ${isScrolled ? 'text-gray-400' : 'text-white'}`}
//               onClick={() => setIsMenuOpen(!isMenuOpen)}
//             >
//               {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
//             </button>
//           </div>

//           <div className="flex-1 flex items-center w-full ">
//             <div className="flex-1">
//               <h1 className={`text-2xl font-bold ${isScrolled ? 'text-gray-100' : 'text-white'}`}
//                 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: isScrolled ? '1.5rem' : '2rem' }}>
//                 {/* Workforce Academy */}
//               </h1>
//               <p className={`text-sm ${isScrolled ? 'text-gray-600' : 'text-orange-200'}`}>
//               </p>
//             </div>

//             <motion.div
//               className="hidden md:inline-flex items-center gap-2 bg-orange-200/20 backdrop-blur-sm border border-orange-500/30 rounded-full px-4 py-2"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.2 }}
//             >
//               <Shield className="" />
//               <span className="text-sm font-semibold text-orange-200">
//                 Trusted Safety Partner Since 2008 - English - Spanish
//               </span>
//             </motion.div>

//             <div className="flex-1"></div>
//           </div>
//         </div>

//         <motion.div
//           className={`md:hidden bg-white shadow-lg ${isMenuOpen ? 'block' : 'hidden'}`}
//           initial={{ opacity: 0, height: 0 }}
//           animate={{
//             opacity: isMenuOpen ? 1 : 0,
//             height: isMenuOpen ? 'auto' : 0
//           }}
//           transition={{ duration: 0.3 }}
//         >
//           <div className="px-4 py-6 space-y-4">
//             {navItems.map((item) => (
//               <a
//                 key={item.name}
//                 href={item.href}
//                 className="block text-gray-700 font-semibold hover:text-orange-500 transition-colors"
//                 onClick={() => setIsMenuOpen(false)}
//               >
//                 {item.name}
//               </a>
//             ))}
//             <button className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full font-semibold">
//               Get Quote
//             </button>
//           </div>
//         </motion.div>
//       </motion.header>
//     </>
//   );
// };

// export default Header;
